import { useCallback, useEffect, useState } from "react";

/**
 * Recharts ResponsiveContainer often measures 0×0 when mounted inside flex/grid
 * panels or right after a display toggle. Measure the container directly instead.
 * Uses a callback ref so measurement starts when the node actually mounts
 * (e.g. after a loading spinner is replaced by the chart).
 */
export function useChartWidth(defaultWidth = 640, defaultHeight = 200) {
    const [node, setNode] = useState(null);
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);
    const containerRef = useCallback((element) => {
        setNode(element);
    }, []);

    useEffect(() => {
        if (!node) {
            return undefined;
        }

        const updateSize = () => {
            const rect = node.getBoundingClientRect();
            const nextWidth = Math.floor(rect.width);
            const nextHeight = Math.floor(rect.height);
            if (nextWidth > 0) {
                setWidth(nextWidth);
            }
            if (nextHeight > 0) {
                setHeight(nextHeight);
            }
        };

        updateSize();
        let innerFrame = 0;
        const frame = requestAnimationFrame(() => {
            updateSize();
            innerFrame = requestAnimationFrame(updateSize);
        });

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(updateSize);
            observer.observe(node);
            window.addEventListener("resize", updateSize);
            return () => {
                cancelAnimationFrame(frame);
                cancelAnimationFrame(innerFrame);
                observer.disconnect();
                window.removeEventListener("resize", updateSize);
            };
        }

        window.addEventListener("resize", updateSize);
        return () => {
            cancelAnimationFrame(frame);
            cancelAnimationFrame(innerFrame);
            window.removeEventListener("resize", updateSize);
        };
    }, [node]);

    return [containerRef, width > 0 ? width : defaultWidth, height > 0 ? height : defaultHeight];
}
